import { ApolloError } from "@apollo/client";
import { useMemo } from "react";
import { FieldError, FormInstance } from "rc-field-form/lib/interface";
import { useIntl } from "react-intl";
import groupBy from "lodash/groupBy";
import { ErrorCode } from "@ticketingplatform/api/dist/const/enums";

const useApolloErrorFields = (
  error?: ApolloError,
  expand?: boolean,
  form?: FormInstance<unknown>
): FieldError[] | undefined => {
  const intl = useIntl();

  return useMemo(
    () =>
      error?.graphQLErrors.reduce<FieldError[]>(
        (fields, { message, errorInfo, errorType }) => {
          const { path } = errorInfo || {};

          if (!path) {
            return fields;
          }

          const errorMessage =
            errorType === ErrorCode.ENTITY_EXISTS
              ? intl.formatMessage(
                  { defaultMessage: "Value of {path} already exists" },
                  { path }
                )
              : message;

          const namePath = path
            .split(/\]\.|\.|\[/)
            .map((part) =>
              /^\d+$/.test(part) ? Number.parseInt(part, 10) : part
            );

          if (!expand) {
            const newFields = [
              ...fields,
              {
                name: namePath,
                errors: [errorMessage],
                warnings: [],
              },
            ];

            form?.setFields(newFields);

            return newFields;
          }

          const subNamePaths = namePath.reduce<(string | number)[][]>(
            (reduced, _item, index, items) => [
              ...reduced,
              items.slice(0, index + 1),
            ],
            []
          );

          const groupedFields = groupBy(
            [
              ...fields,
              ...subNamePaths.map((name) => ({
                name,
                errors: [errorMessage],
                warnings: [],
              })),
            ],
            "name"
          );

          const newFields = Object.values(groupedFields).map(
            ([{ name, errors }, ...restFields]) => ({
              name,
              warnings: [],
              errors: [
                ...errors,
                ...restFields.flatMap((field) => field.errors),
              ],
            })
          );

          form?.setFields(newFields);

          return newFields;
        },
        []
      ),
    [error?.graphQLErrors, expand, form, intl]
  );
};

export default useApolloErrorFields;
